/**
 * MobX store for Platform Transformation cutover (second Shopify store for the same Global-e MID).
 */
import { makeAutoObservable, runInAction } from "mobx";
import AdminMigrateGlobalEMerchantsService, {
    IGlobaleStoreMetaFields,
    IPlatformTransformationResponse,
} from "../../../../services/api/admin-migrate-global-e-merchants-service";
import { rootContainer, TYPE } from "../../../../services/container/root";
import toastMessageListStore from "../../../../components/toast-messages-modal/toast-messages-store";
import { TOAST_VARIANTS } from "../../../../components/toast-messages-modal/toast-message/toast-message";
import { IShop } from "../../../../models/User";
import { isSet } from "../../../../models/model-types-methods";

function normalizeMerchantId(value: string | number | null | undefined): string {
    return String(value ?? "").trim();
}

class PlatformTransformationStore {
    private _oldPortal: IShop[] = [];
    private _newPortal: IShop[] = [];
    private _newStoreMetaFields?: IGlobaleStoreMetaFields;
    private _isFetchingMetaFields = false;
    private _isProcessing = false;
    private _lastResult: IPlatformTransformationResponse | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): AdminMigrateGlobalEMerchantsService {
        return rootContainer.get<AdminMigrateGlobalEMerchantsService>(TYPE.AdminMigrateGlobalEMerchantsService);
    }

    public get oldPortal(): IShop[] {
        return this._oldPortal;
    }

    public get newPortal(): IShop[] {
        return this._newPortal;
    }

    public get isFetchingMetaFields(): boolean {
        return this._isFetchingMetaFields;
    }

    public get isProcessing(): boolean {
        return this._isProcessing;
    }

    public get lastResult(): IPlatformTransformationResponse | null {
        return this._lastResult;
    }

    private globaleIdentityMatches(): boolean {
        const oldShop = this._oldPortal[0];
        if (!isSet(oldShop) || !this._newStoreMetaFields) {
            return false;
        }

        const oldMid = normalizeMerchantId(oldShop.global_e_user_id);
        const oldEnv = (oldShop.global_e_env ?? "").trim();
        if (!oldMid || !oldEnv) {
            return false;
        }

        const newMid = normalizeMerchantId(this._newStoreMetaFields.merchant_id);
        const newEnv = String(this._newStoreMetaFields.env ?? "").trim();

        return oldMid === newMid && oldEnv === newEnv;
    }

    public get isValid(): boolean {
        const oldShop = this._oldPortal[0];
        const newShop = this._newPortal[0];
        if (!isSet(oldShop) || !isSet(newShop)) {
            return false;
        }
        if (oldShop.id === newShop.id) {
            return false;
        }
        return this.globaleIdentityMatches();
    }

    public setOldPortal(shops: IShop[]): void {
        this._oldPortal = shops;
        this._lastResult = null;
    }

    public setNewPortal(shops: IShop[]): void {
        this._newPortal = shops;
        this._newStoreMetaFields = undefined;
        this._lastResult = null;
        void this.fetchNewStoreMetaFields();
    }

    public async submit(): Promise<void> {
        if (!this.isValid || this._isProcessing) {
            return;
        }

        const oldShop = this._oldPortal[0];
        const newShop = this._newPortal[0];
        if (!isSet(oldShop) || !isSet(newShop)) {
            return;
        }

        try {
            this._isProcessing = true;
            this._lastResult = null;
            const result = await this.service.runPlatformTransformation({
                old_portal_id: oldShop.id,
                new_portal_id: newShop.id,
            });
            runInAction(() => {
                this._lastResult = result;
            });
            toastMessageListStore.add({
                title: result.success ? "Success" : "Failed",
                description: result.success
                    ? `Platform transformation completed for portal #${oldShop.id} → #${newShop.id}.`
                    : `Platform transformation failed for portal #${oldShop.id} → #${newShop.id}.`,
                variant: result.success ? TOAST_VARIANTS.SUCCESS : TOAST_VARIANTS.ERROR,
            });
            if (result.success) {
                this.resetPortals();
            }
        } catch (e) {
            console.error(e);
        } finally {
            runInAction(() => {
                this._isProcessing = false;
            });
        }
    }

    private resetPortals(): void {
        this._oldPortal = [];
        this._newPortal = [];
        this._newStoreMetaFields = undefined;
    }

    private async fetchNewStoreMetaFields(): Promise<void> {
        const newShop = this._newPortal[0];
        if (!isSet(newShop)) {
            this._newStoreMetaFields = undefined;
            return;
        }

        try {
            this._isFetchingMetaFields = true;
            const metaFields = await this.service.fetchGlobaleStoreMetaFields(newShop.id);
            runInAction(() => {
                this._newStoreMetaFields = metaFields;
            });
        } catch (e) {
            console.error(e);
            runInAction(() => {
                this._newStoreMetaFields = undefined;
            });
        } finally {
            runInAction(() => {
                this._isFetchingMetaFields = false;
            });
        }
    }
}

export default new PlatformTransformationStore();
